import React, { useMemo, useState } from 'react';
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Modal as BootstrapModal,
  Form as BootstrapForm,
} from 'react-bootstrap';
import { Table, Select, DatePicker, Form, Tag, App } from 'antd';
import { FilterOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import LifecycleRulesDrawer, {
  LifecycleRulesInfoButton,
} from '@/common/LifecycleRulesDrawer/LifecycleRulesDrawer';
import { ARTISTS_LIFECYCLE_RULES } from '@/features/lifecycle-rules/lifecycleRulesContent';
import type { ColumnType, TableRowSelection } from 'antd/es/table/interface';
import PageTitle from '@/common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import styles from './artists.module.scss';

import type { Artist, ArtistStatus } from '@/features/artists/artistsApi';
import {
  useListArtistsQuery,
  useCreateArtistMutation,
  useUpdateArtistMutation,
  useArchiveArtistMutation,
  useRestoreArtistMutation,
  usePreflightPurgeArtistMutation,
  usePurgeArtistMutation,
  useBulkPurgeArtistsMutation,
  extractArtistErrorMessage,
} from '@/features/artists/artistsApi';
import {
  CatalogLifecycleTabs,
  CatalogArchiveModal,
  CatalogRestoreModal,
  CatalogBatchConfirmModal,
  CatalogPermanentDeleteModal,
  CatalogPurgeFailuresModal,
  useCatalogPurgeFlow,
  useBatchActions,
  catalogBatchOptions,
  mixedSelectionWarning,
  type CatalogLifecycleView,
  type BatchActionValue,
  type SelectionLifecycle,
} from '@/features/catalog-lifecycle';
import {
  useCanEditPage,
  useCanPerformLifecycleDelete,
} from '@/contexts/SuperAdminIdentityContext';
import { Link, useSearchParams } from 'react-router-dom';

const PAGE_ID = 'artists';
const KIND = 'artist' as const;

type ArtistRecord = {
  key: string;
  artistId: string;
  artistName: string;
  firstNameRaw: string;
  lastNameRaw: string;
  status: ArtistStatus;
  songCount: number;
  createdBySource: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  biographyRaw: string;
};

// Empty editor state matches the Channel Admin "Add New Channel" form
// pattern: a plain object held in component state, fields cleared on
// modal cancel/dismiss. The composite invariant ("name OR firstName OR
// lastName must be non-blank") is enforced both client-side (primary
// button disabled until satisfied) and server-side (400 if not).
type ArtistEditorState = {
  name: string;
  firstName: string;
  lastName: string;
  biography: string;
};

const EMPTY_EDITOR: ArtistEditorState = {
  name: '',
  firstName: '',
  lastName: '',
  biography: '',
};

function toArtistRecord(a: Artist): ArtistRecord {
  return {
    key: a.artistId,
    artistId: a.artistId,
    artistName: a.name,
    firstNameRaw: a.firstName ?? '',
    lastNameRaw: a.lastName ?? '',
    status: a.status,
    songCount: a.activeSongCount ?? 0,
    createdBySource: a.createdBySource ?? '',
    createdAt: dayjs(a.createdAt).format('YYYY-MM-DD'),
    updatedAt: dayjs(a.updatedAt).format('YYYY-MM-DD'),
    deletedAt: a.deletedAt,
    biographyRaw: a.biography ?? '',
  };
}

function formatStatusLabel(status: ArtistStatus): string {
  return status === 'deleted' ? 'Archived' : 'Active';
}

/** UI lifecycle view → Artists API status (archived is spelled `deleted`). */
function apiStatusForView(view: CatalogLifecycleView): ArtistStatus | 'all' {
  if (view === 'archived') return 'deleted';
  return view;
}

function rowSelectionLifecycle(status: ArtistStatus): 'active' | 'archived' {
  return status === 'deleted' ? 'archived' : 'active';
}

function isEditorValid(state: ArtistEditorState): boolean {
  const n = state.name.trim();
  const f = state.firstName.trim();
  const l = state.lastName.trim();
  return n.length > 0 || f.length > 0 || l.length > 0;
}

const Artists: React.FC = () => {
  const { message } = App.useApp();
  const canEdit = useCanEditPage(PAGE_ID);
  const canLifecycle = useCanPerformLifecycleDelete();

  const [view, setView] = useState<CatalogLifecycleView>('active');
  const [lifecycleHelpOpen, setLifecycleHelpOpen] = useState(false);

  const listStatus = apiStatusForView(view);
  const { data, isLoading, isFetching } = useListArtistsQuery({
    status: listStatus,
    limit: 200,
  });
  // Tab counts: dedicated count endpoints are not available for Artists, so
  // we load Active + Archived lists and use their lengths (Song Library
  // fallback pattern).
  const { data: activeData } = useListArtistsQuery({
    status: 'active',
    limit: 200,
  });
  const { data: archivedData } = useListArtistsQuery({
    status: 'deleted',
    limit: 200,
  });

  const [createArtist, createState] = useCreateArtistMutation();
  const [updateArtist, updateState] = useUpdateArtistMutation();
  const [archiveArtist] = useArchiveArtistMutation();
  const [restoreArtist] = useRestoreArtistMutation();
  const [preflightPurgeArtist] = usePreflightPurgeArtistMutation();
  const [purgeArtist] = usePurgeArtistMutation();
  const [bulkPurgeArtists] = useBulkPurgeArtistsMutation();

  const artists: ArtistRecord[] = useMemo(
    () => (data?.artists ?? []).map((a) => toArtistRecord(a)),
    [data],
  );

  const activeCount = activeData?.artists?.length;
  const archivedCount = archivedData?.artists?.length;

  const filterOptions = useMemo(
    () => ({
      artistNames: artists
        .map((a) => a.artistName)
        .sort((x, y) => x.localeCompare(y)),
      // Song-name filter is visual-only until track↔artist linkage ships.
      songNames: [] as string[],
    }),
    [artists],
  );

  // Filter state. `?name=` deep-links from other catalog pages (Song
  // Library / Albums artist cells) prefill the artist-name filter.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    artistName: searchParams.get('name') ?? undefined,
    songName: undefined as string | undefined,
    createdAt: undefined as string | undefined,
    updatedAt: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  const {
    selectedRowKeys,
    setSelectedRowKeys,
    batchAction,
    batchModalOpen,
    chooseBatchAction,
    resetBatchUi,
  } = useBatchActions();

  const purgeFlow = useCatalogPurgeFlow({
    kind: KIND,
    canLifecycle,
    message,
    preflight: async (id) =>
      preflightPurgeArtist({ artistId: id }).unwrap(),
    purge: async ({ id, name, reason }) =>
      purgeArtist({
        artistId: id,
        reason,
        confirmName: name,
        confirmArtistId: id,
      }).unwrap(),
    bulkPurge: async ({ ids, reason }) =>
      bulkPurgeArtists({ ids, reason }).unwrap(),
    extractErrorMessage: extractArtistErrorMessage,
    resetBatchUi,
  });

  // Checkboxes on every lifecycle tab when the operator can lifecycle-delete
  // (owner UX — never gate on Active-only).
  const rowSelection: TableRowSelection<ArtistRecord> | undefined = canLifecycle
    ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
      }
    : undefined;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addEditor, setAddEditor] = useState<ArtistEditorState>(EMPTY_EDITOR);

  const [editingArtist, setEditingArtist] = useState<ArtistRecord | null>(null);
  const [editEditor, setEditEditor] = useState<ArtistEditorState>(EMPTY_EDITOR);
  const editModalOpen = editingArtist != null;

  const [viewingArtist, setViewingArtist] = useState<ArtistRecord | null>(null);
  const viewModalOpen = viewingArtist != null;

  const [archivingArtist, setArchivingArtist] = useState<ArtistRecord | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [restoringArtist, setRestoringArtist] = useState<ArtistRecord | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [batchLoading, setBatchLoading] = useState(false);

  // Filtering logic (matches original — songName is intentionally not
  // applied as a filter; the dropdown is visual-only because track data
  // linked to artists is not yet available for client-side filtering).
  const filteredArtists = artists.filter((artist) => {
    return (
      (!filters.artistName || artist.artistName === filters.artistName) &&
      (!filters.createdAt || artist.createdAt === filters.createdAt) &&
      (!filters.updatedAt || artist.updatedAt === filters.updatedAt)
    );
  });

  const selectedRows = useMemo(
    () => filteredArtists.filter((a) => selectedRowKeys.includes(a.key)),
    [filteredArtists, selectedRowKeys],
  );

  const selectionLifecycle: SelectionLifecycle = useMemo(() => {
    if (selectedRows.length === 0) return 'none';
    const statuses = new Set(selectedRows.map((r) => rowSelectionLifecycle(r.status)));
    if (statuses.size > 1) return 'mixed';
    return selectedRows[0].status === 'deleted' ? 'archived' : 'active';
  }, [selectedRows]);

  // Status-scoped subsets of the current selection — mixed All-tab
  // selections run each batch action against its matching subset only
  // (owner-approved 2026-08-11, Song Library parity).
  const selectedActiveIds = useMemo(
    () =>
      selectedRows
        .filter((r) => rowSelectionLifecycle(r.status) === 'active')
        .map((r) => r.key),
    [selectedRows],
  );
  const selectedArchivedIds = useMemo(
    () =>
      selectedRows
        .filter((r) => rowSelectionLifecycle(r.status) === 'archived')
        .map((r) => r.key),
    [selectedRows],
  );

  const batchOptions = useMemo(
    () =>
      catalogBatchOptions(view, selectionLifecycle, {
        active: selectedActiveIds.length,
        archived: selectedArchivedIds.length,
      }),
    [view, selectionLifecycle, selectedActiveIds.length, selectedArchivedIds.length],
  );

  /** Ids the latched batch action will run against (scoped when mixed). */
  const batchTargetIds = useMemo(() => {
    if (selectionLifecycle === 'mixed') {
      if (batchAction === 'archive') return selectedActiveIds;
      if (batchAction === 'restore') return selectedArchivedIds;
      return [];
    }
    return selectedRowKeys.map(String);
  }, [selectionLifecycle, batchAction, selectedActiveIds, selectedArchivedIds, selectedRowKeys]);

  const switchView = (next: CatalogLifecycleView) => {
    setView(next);
    resetBatchUi();
  };

  const handleBatchAction = (value: BatchActionValue) => {
    if (!canLifecycle) return;
    chooseBatchAction(value, selectionLifecycle, {
      onMixedSelection: () => {
        message.warning(mixedSelectionWarning('artists'));
      },
      onDelete: (ids) => void purgeFlow.openBulk(ids),
      mixedScope: {
        activeIds: selectedActiveIds,
        archivedIds: selectedArchivedIds,
      },
    });
  };

  const handleConfirmBatch = async () => {
    if (!canLifecycle || !batchAction) return;
    if (batchAction !== 'archive' && batchAction !== 'restore') return;
    if (batchTargetIds.length === 0) return;

    setBatchLoading(true);
    let failures = 0;
    for (const key of batchTargetIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        if (batchAction === 'archive') {
          await archiveArtist({ artistId: String(key) }).unwrap();
        } else {
          await restoreArtist({ artistId: String(key) }).unwrap();
        }
      } catch {
        failures += 1;
      }
    }
    const succeeded = batchTargetIds.length - failures;
    if (batchAction === 'archive') {
      if (succeeded > 0) {
        message.success(
          `Archived ${succeeded} artist${succeeded === 1 ? '' : 's'} — moved to Archived Artists`,
        );
      }
      if (failures > 0) message.error(`Failed to archive ${failures} artist(s)`);
    } else {
      if (succeeded > 0) {
        message.success(
          `Restored ${succeeded} artist${succeeded === 1 ? '' : 's'} — moved to Active Artists`,
        );
      }
      if (failures > 0) message.error(`Failed to restore ${failures} artist(s)`);
    }
    setBatchLoading(false);
    resetBatchUi();
  };

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      artistName: undefined,
      songName: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    });
    setView('active');
    // Match Albums: Reset All returns to Active and must drop selection so
    // Batch Actions cannot fire against stale Archived keys.
    resetBatchUi();
    setResetCounter((prev) => prev + 1);
  };

  const handleCancelAdd = () => {
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(false);
  };

  const openAdd = () => {
    if (!canEdit) return;
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(true);
  };

  const submitAdd = async () => {
    if (!isEditorValid(addEditor)) return;
    const trimmedName = addEditor.name.trim();
    const trimmedFirst = addEditor.firstName.trim();
    const trimmedLast = addEditor.lastName.trim();
    const trimmedBio = addEditor.biography.trim();
    // Send only fields the user actually populated so the server
    // controls derivation. If name is blank but first/last has content,
    // we omit `name` entirely — the backend derives it.
    const payload: {
      name?: string;
      firstName?: string;
      lastName?: string;
      biography?: string;
    } = {};
    if (trimmedName.length > 0) payload.name = trimmedName;
    if (trimmedFirst.length > 0) payload.firstName = trimmedFirst;
    if (trimmedLast.length > 0) payload.lastName = trimmedLast;
    if (trimmedBio.length > 0) payload.biography = trimmedBio;
    try {
      const created = await createArtist(payload).unwrap();
      message.success(`Created artist "${created.name}"`);
      setAddEditor(EMPTY_EDITOR);
      setAddModalOpen(false);
    } catch (err: unknown) {
      const msg = extractArtistErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const openEdit = (record: ArtistRecord) => {
    if (!canEdit || record.status !== 'active') return;
    setEditingArtist(record);
    setEditEditor({
      name: record.artistName,
      firstName: record.firstNameRaw,
      lastName: record.lastNameRaw,
      biography: record.biographyRaw,
    });
  };

  const openView = (record: ArtistRecord) => {
    setViewingArtist(record);
  };

  const handleCancelEdit = () => {
    setEditEditor(EMPTY_EDITOR);
    setEditingArtist(null);
  };

  const submitEdit = async () => {
    if (!editingArtist) return;
    if (!isEditorValid(editEditor)) return;
    const trimmedName = editEditor.name.trim();
    const trimmedFirst = editEditor.firstName.trim();
    const trimmedLast = editEditor.lastName.trim();
    const trimmedBio = editEditor.biography.trim();
    // Build a sparse PATCH body of only the fields that actually
    // differ from the existing record. PATCH-classic for first/last:
    // sending "" explicitly clears; absent leaves alone. For `name`,
    // sending "" is the explicit-clear signal that asks the backend to
    // re-derive from merged first/last.
    const patch: {
      name?: string;
      firstName?: string | null;
      lastName?: string | null;
      biography?: string | null;
    } = {};
    if (trimmedName !== editingArtist.artistName) {
      patch.name = trimmedName; // may be "" -> backend re-derives
    }
    if (trimmedFirst !== editingArtist.firstNameRaw) {
      patch.firstName = trimmedFirst === '' ? null : trimmedFirst;
    }
    if (trimmedLast !== editingArtist.lastNameRaw) {
      patch.lastName = trimmedLast === '' ? null : trimmedLast;
    }
    if (trimmedBio !== editingArtist.biographyRaw) {
      patch.biography = trimmedBio === '' ? null : trimmedBio;
    }
    if (Object.keys(patch).length === 0) {
      setEditEditor(EMPTY_EDITOR);
      setEditingArtist(null);
      return;
    }
    try {
      const updated = await updateArtist({
        artistId: editingArtist.artistId,
        ...patch,
      }).unwrap();
      message.success(`Updated artist "${updated.name}"`);
      setEditEditor(EMPTY_EDITOR);
      setEditingArtist(null);
    } catch (err: unknown) {
      const msg = extractArtistErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const submitArchive = async () => {
    if (!archivingArtist) return;
    try {
      setArchiveLoading(true);
      await archiveArtist({ artistId: archivingArtist.artistId }).unwrap();
      message.success('Artist archived — moved to Archived Artists');
      setArchivingArtist(null);
    } catch (err: unknown) {
      const msg = extractArtistErrorMessage(err);
      if (msg) message.error(msg);
    } finally {
      setArchiveLoading(false);
    }
  };

  const submitRestore = async () => {
    if (!restoringArtist) return;
    try {
      setRestoreLoading(true);
      await restoreArtist({ artistId: restoringArtist.artistId }).unwrap();
      message.success('Artist restored — moved to Active Artists');
      setRestoringArtist(null);
    } catch (err: unknown) {
      const msg = extractArtistErrorMessage(err);
      if (msg) message.error(msg);
    } finally {
      setRestoreLoading(false);
    }
  };

  const columns: ColumnType<ArtistRecord>[] = [
    {
      title: 'Artists Name',
      dataIndex: 'artistName',
      sorter: (a: ArtistRecord, b: ArtistRecord) => a.artistName.localeCompare(b.artistName),
      width: 220,
      render: (_: unknown, record: ArtistRecord) => (
        <span>
          {record.artistName}
          {record.createdBySource === 'pipeline' && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              Auto-created
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Songs',
      dataIndex: 'songCount',
      sorter: (a: ArtistRecord, b: ArtistRecord) => a.songCount - b.songCount,
      width: 80,
      render: (count: number, record: ArtistRecord) => (
        <Link to={`/songs?artistId=${encodeURIComponent(record.artistId)}&status=active`}>
          {count}
        </Link>
      ),
    },
    ...(view !== 'active'
      ? [
          {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (status: ArtistStatus) => formatStatusLabel(status),
          } as ColumnType<ArtistRecord>,
        ]
      : []),
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a: ArtistRecord, b: ArtistRecord) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 140,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      sorter: (a: ArtistRecord, b: ArtistRecord) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_: unknown, record: ArtistRecord) => {
        const isActive = record.status === 'active';
        const isArchived = record.status === 'deleted';

        return (
          <span>
            <a
              style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
              onClick={() => openView(record)}
            >
              View
            </a>
            {canEdit && isActive ? (
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() => openEdit(record)}
              >
                Edit
              </a>
            ) : null}
            {canLifecycle && isActive ? (
              <a
                style={{ marginRight: 12, color: '#e21e26', cursor: 'pointer' }}
                onClick={() => setArchivingArtist(record)}
              >
                Archive
              </a>
            ) : null}
            {canLifecycle && isArchived ? (
              <>
                <a
                  style={{ marginRight: 12, color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => setRestoringArtist(record)}
                >
                  Restore
                </a>
                <a
                  style={{ color: '#e21e26', cursor: 'pointer' }}
                  onClick={() =>
                    void purgeFlow.openSingle(record.artistId, record.artistName)
                  }
                >
                  Permanent Delete
                </a>
              </>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <PageTitle title="Artists" />
              <LifecycleRulesInfoButton onClick={() => setLifecycleHelpOpen(true)} />
            </span>
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <CatalogLifecycleTabs
            view={view}
            onChange={switchView}
            activeCount={activeCount}
            archivedCount={archivedCount}
            testId="artists-lifecycle-tabs"
          />

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Search by artist name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`artistName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any artist name"
                            value={filters.artistName}
                            onChange={(value) => handleFilterChange('artistName', value)}
                            options={filterOptions.artistNames.map((name) => ({
                              label: name,
                              value: name,
                            }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Search by song name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`songName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any song name"
                            value={filters.songName}
                            onChange={(value) => handleFilterChange('songName', value)}
                            options={filterOptions.songNames.map((name) => ({
                              label: name,
                              value: name,
                            }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Created at"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`createdAt-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.dateField}`}
                            placeholder="Created at"
                            value={filters.createdAt ? dayjs(filters.createdAt) : undefined}
                            onChange={(date) =>
                              handleFilterChange(
                                'createdAt',
                                date ? date.format('YYYY-MM-DD') : undefined,
                              )
                            }
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Updated at"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`updatedAt-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.dateField}`}
                            placeholder="Updated at"
                            value={filters.updatedAt ? dayjs(filters.updatedAt) : undefined}
                            onChange={(date) =>
                              handleFilterChange(
                                'updatedAt',
                                date ? date.format('YYYY-MM-DD') : undefined,
                              )
                            }
                          />
                        </FormItem>
                      </BootstrapCol>
                      {canLifecycle ? (
                        <BootstrapCol md={4}>
                          <FormItem
                            label="Batch Actions"
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <Select<BatchActionValue>
                              key={`batchActions-reset-${resetCounter}`}
                              className={`customSelect ${styles.selectField}`}
                              placeholder="Batch Actions"
                              // `null` (never undefined) keeps this Select
                              // controlled so antd cannot latch a stale value —
                              // see useBatchActions.ts.
                              value={batchAction}
                              onChange={handleBatchAction}
                              disabled={
                                selectedRowKeys.length === 0 ||
                                batchOptions.length === 0
                              }
                              options={batchOptions}
                            />
                          </FormItem>
                        </BootstrapCol>
                      ) : null}
                      {canEdit ? (
                        <BootstrapCol
                          md={canLifecycle ? 4 : 8}
                          className="d-flex align-items-center mt-2 mt-md-0"
                        >
                          <Button
                            className="w-100"
                            style={{
                              background: 'white',
                              color: '#e21e26',
                              border: '1px solid #e21e26',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              justifyContent: 'center',
                              height: '50px',
                              marginTop: '8px',
                            }}
                            onClick={openAdd}
                          >
                            <PlusOutlined style={{ fontSize: 16 }} />
                            Add New Artist
                          </Button>
                        </BootstrapCol>
                      ) : null}
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredArtists}
            pagination={{ pageSize: 10, showQuickJumper: true }}
            rowKey="key"
            bordered
            scroll={{ x: true }}
            loading={{
              spinning: isLoading || isFetching,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            }}
          />
        </Card.Body>
      </Card>

      {/* Add New Artist Modal — react-bootstrap to match Channel Admin styling */}
      <BootstrapModal show={addModalOpen} onHide={handleCancelAdd} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Add New Artist</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <BootstrapForm>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Artist Name *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter artist name"
                value={addEditor.name}
                maxLength={256}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              <BootstrapForm.Text className="text-muted">
                Required unless First Name or Last Name is provided. Works
                for bands, mononyms, DJ aliases, and any other artist shape.
              </BootstrapForm.Text>
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>First Name</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Optional"
                value={addEditor.firstName}
                maxLength={256}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, firstName: e.target.value }))
                }
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Last Name</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Optional"
                value={addEditor.lastName}
                maxLength={256}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, lastName: e.target.value }))
                }
              />
              <BootstrapForm.Text className="text-muted">
                If you leave Artist Name blank, it will be set to First +
                Last automatically.
              </BootstrapForm.Text>
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Biography</BootstrapForm.Label>
              <BootstrapForm.Control
                as="textarea"
                rows={4}
                placeholder="Optional"
                value={addEditor.biography}
                maxLength={4096}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, biography: e.target.value }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapForm>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCancelAdd}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitAdd}
            disabled={!isEditorValid(addEditor) || createState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {createState.isLoading ? 'Creating…' : 'Create Artist'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Edit Artist Modal — same Channel-Admin styling */}
      <BootstrapModal show={editModalOpen} onHide={handleCancelEdit} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Edit Artist</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <BootstrapForm>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Artist Name *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter artist name"
                value={editEditor.name}
                maxLength={256}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              <BootstrapForm.Text className="text-muted">
                Required unless First Name or Last Name is provided. Clearing
                this field will re-derive it from First + Last.
              </BootstrapForm.Text>
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>First Name</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Optional"
                value={editEditor.firstName}
                maxLength={256}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, firstName: e.target.value }))
                }
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Last Name</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Optional"
                value={editEditor.lastName}
                maxLength={256}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, lastName: e.target.value }))
                }
              />
            </BootstrapForm.Group>

            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Biography</BootstrapForm.Label>
              <BootstrapForm.Control
                as="textarea"
                rows={4}
                placeholder="Optional"
                value={editEditor.biography}
                maxLength={4096}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, biography: e.target.value }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapForm>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitEdit}
            disabled={!isEditorValid(editEditor) || updateState.isLoading}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {updateState.isLoading ? 'Saving…' : 'Save Changes'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <BootstrapModal show={viewModalOpen} onHide={() => setViewingArtist(null)} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Artist Details</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {viewingArtist ? (
            <BootstrapForm>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Artist Name</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.artistName}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>First Name</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.firstNameRaw || '—'}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Last Name</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.lastNameRaw || '—'}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Artist ID</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.artistId}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Status</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={formatStatusLabel(viewingArtist.status)}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Active Songs</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={String(viewingArtist.songCount)}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Created At</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.createdAt}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Updated At</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingArtist.updatedAt}
                />
              </BootstrapForm.Group>
              {viewingArtist.biographyRaw ? (
                <BootstrapForm.Group className="mb-3">
                  <BootstrapForm.Label>Biography</BootstrapForm.Label>
                  <BootstrapForm.Control
                    as="textarea"
                    rows={3}
                    readOnly
                    value={viewingArtist.biographyRaw}
                  />
                </BootstrapForm.Group>
              ) : null}
            </BootstrapForm>
          ) : null}
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setViewingArtist(null)}>
            Close
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <CatalogArchiveModal
        kind={KIND}
        open={archivingArtist != null}
        name={archivingArtist?.artistName}
        loading={archiveLoading}
        onCancel={() => setArchivingArtist(null)}
        onConfirm={() => void submitArchive()}
      />

      <CatalogRestoreModal
        kind={KIND}
        open={restoringArtist != null}
        name={restoringArtist?.artistName}
        loading={restoreLoading}
        onCancel={() => setRestoringArtist(null)}
        onConfirm={() => void submitRestore()}
      />

      <CatalogBatchConfirmModal
        kind={KIND}
        open={batchModalOpen}
        action={batchAction === 'archive' || batchAction === 'restore' ? batchAction : null}
        selectedCount={batchTargetIds.length}
        mixedScoped={selectionLifecycle === 'mixed'}
        loading={batchLoading}
        onCancel={() => resetBatchUi()}
        onConfirm={() => void handleConfirmBatch()}
      />

      <CatalogPermanentDeleteModal {...purgeFlow.modalProps} />
      <CatalogPurgeFailuresModal {...purgeFlow.failuresProps} />

      <LifecycleRulesDrawer
        content={ARTISTS_LIFECYCLE_RULES}
        open={lifecycleHelpOpen}
        onClose={() => setLifecycleHelpOpen(false)}
      />
    </Container>
  );
};

export default withAuthenticationRequired(Artists);
